// bluaIQ — Plans & Markups sheet export helpers.
//
// Set-agnostic compositing/PDF helpers; the CALLER (DrawingMarkup) passes only the
// ACTIVE plan set's sheets/elements, so the marked-up PDF is set-scoped (PR-PM4).
// For the CURRENT sheet the overlay is taken from the live canvas SVG (pixel-
// match); for OTHER sheets in the set it is built programmatically from the
// sheet's visible elements (only the active sheet is rendered live).
//
// PDFs use `pdf-lib` (already a dependency), loaded lazily so it stays out of the
// main bundle. Fully client-side, no network/endpoint, no public link; background
// images load from the same-origin token-protected /api/uploads URL.

const VB_W = 700
const VB_H = 420

export interface ExportElement {
  id: string
  type: string
  points?: { x: number; y: number }[]
  x?: number
  y?: number
  width?: number
  height?: number
  text?: string
  color?: string
  strokeWidth?: number
}

function xmlEscape(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image load failed'))
    img.src = src
  })
}

// Programmatic markup overlay (mirrors the on-screen renderElement) for a sheet's
// VISIBLE elements. Vector-only (no external refs) so it composites without taint.
export function renderMarkupOverlaySvg(elements: ExportElement[], w = VB_W, h = VB_H): string {
  const defs: string[] = []
  const parts: string[] = []
  for (const el of elements) {
    const color = el.color || '#000000'
    const sw = el.strokeWidth ?? 2
    switch (el.type) {
      case 'pen':
        if (!el.points || el.points.length < 2) break
        parts.push(`<polyline points="${el.points.map(p => `${p.x},${p.y}`).join(' ')}" fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>`)
        break
      case 'line':
        if (!el.points || el.points.length < 2) break
        parts.push(`<line x1="${el.points[0].x}" y1="${el.points[0].y}" x2="${el.points[1].x}" y2="${el.points[1].y}" stroke="${color}" stroke-width="${sw}"/>`)
        break
      case 'arrow': {
        if (!el.points || el.points.length < 2) break
        const mid = `arrow-${xmlEscape(el.id)}`
        defs.push(`<marker id="${mid}" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 Z" fill="${color}"/></marker>`)
        parts.push(`<line x1="${el.points[0].x}" y1="${el.points[0].y}" x2="${el.points[1].x}" y2="${el.points[1].y}" stroke="${color}" stroke-width="${sw}" marker-end="url(#${mid})"/>`)
        break
      }
      case 'rectangle':
        parts.push(`<rect x="${el.x}" y="${el.y}" width="${el.width}" height="${el.height}" fill="none" stroke="${color}" stroke-width="${sw}"/>`)
        break
      case 'circle':
        parts.push(`<ellipse cx="${el.x}" cy="${el.y}" rx="${(el.width || 0) / 2}" ry="${(el.height || 0) / 2}" fill="none" stroke="${color}" stroke-width="${sw}"/>`)
        break
      case 'text':
        parts.push(`<text x="${el.x}" y="${el.y}" fill="${color}" font-size="13" font-weight="700" font-family="sans-serif">${xmlEscape(el.text || '')}</text>`)
        break
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${defs.length ? `<defs>${defs.join('')}</defs>` : ''}${parts.join('')}</svg>`
}

// Clone the live canvas SVG and strip the raster background + grid, leaving only
// the markup elements (self-contained vector SVG).
function buildOverlayFromLiveSvg(svgEl: SVGSVGElement): string {
  const clone = svgEl.cloneNode(true) as SVGSVGElement
  clone.setAttribute('width', String(VB_W))
  clone.setAttribute('height', String(VB_H))
  clone.querySelectorAll('image').forEach(n => n.remove())
  clone.querySelectorAll('defs').forEach(n => n.remove())
  clone.querySelectorAll('rect').forEach(n => {
    if ((n.getAttribute('fill') || '').includes('url(#grid)')) n.remove()
  })
  return new XMLSerializer().serializeToString(clone)
}

// Composite a background image + a vector overlay SVG into a PNG data URL.
async function compositeToDataUrl(backgroundHref: string, overlaySvg: string, scale = 2): Promise<string> {
  const canvas = document.createElement('canvas')
  canvas.width = VB_W * scale
  canvas.height = VB_H * scale
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  ctx.scale(scale, scale)
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, VB_W, VB_H)

  if (backgroundHref) {
    const bg = await loadImage(backgroundHref)
    const imgRatio = bg.width / bg.height
    const boxRatio = VB_W / VB_H
    let dw: number, dh: number, dx: number, dy: number
    if (imgRatio > boxRatio) { dw = VB_W; dh = VB_W / imgRatio; dx = 0; dy = (VB_H - dh) / 2 }
    else { dh = VB_H; dw = VB_H * imgRatio; dx = (VB_W - dw) / 2; dy = 0 }
    ctx.drawImage(bg, dx, dy, dw, dh)
  }

  const overlayImg = await loadImage('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(overlaySvg))
  ctx.drawImage(overlayImg, 0, 0, VB_W, VB_H)
  return canvas.toDataURL('image/png')
}

// Current sheet (pixel-match): composite from the live SVG.
export async function composeSheetPngDataUrl(svgEl: SVGSVGElement, backgroundHref: string, scale = 2): Promise<string> {
  return compositeToDataUrl(backgroundHref, buildOverlayFromLiveSvg(svgEl), scale)
}

// Any sheet in the set: composite from the sheet's (visible) elements.
export async function composeSheetPngFromElements(
  backgroundHref: string,
  elements: ExportElement[],
  scale = 2,
): Promise<string> {
  return compositeToDataUrl(backgroundHref, renderMarkupOverlaySvg(elements), scale)
}

// Build a PDF (one page per composited image) using pdf-lib. Each page is sized
// to its image and gets a footer with the sheet name/number.
export async function buildImagesPdfBlob(pages: { dataUrl: string; label: string }[]): Promise<Blob> {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')
  const doc = await PDFDocument.create()
  const font = await doc.embedFont(StandardFonts.Helvetica)
  const FOOTER = 24
  for (const pg of pages) {
    const bytes = await (await fetch(pg.dataUrl)).arrayBuffer()
    const png = await doc.embedPng(bytes)
    const page = doc.addPage([png.width, png.height + FOOTER])
    page.drawImage(png, { x: 0, y: FOOTER, width: png.width, height: png.height })
    page.drawText(pg.label, { x: 8, y: 8, size: 10, font, color: rgb(0.2, 0.2, 0.2) })
  }
  const out = await doc.save()
  return new Blob([out as unknown as BlobPart], { type: 'application/pdf' })
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// Read a Blob into a base64 `data:` URL. Used on iOS Safari for PDF export, where
// `data:` URLs get the SAME reliable open/save flow the current-sheet IMAGE export
// already uses — unlike `blob:` object URLs, which iOS opens in the same tab and
// can throw WebKitBlobResource error. Self-contained: no createObjectURL, nothing
// to revoke.
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('blob read failed'))
    reader.readAsDataURL(blob)
  })
}

// Download a PDF blob with the safest per-platform path.
// - iOS Safari: convert to a `data:application/pdf` URL and use the same anchor
//   flow as the image export (downloadDataUrl) → opens the OS share/save viewer
//   reliably, no same-tab trap, no WebKitBlobResource error (no blob: URL at all).
// - Desktop / everything else: the blob + `download` attribute saves the file
//   directly (unchanged behavior).
export async function downloadPdfBlobSmart(blob: Blob, filename: string, isIos: boolean): Promise<void> {
  if (isIos) {
    const dataUrl = await blobToDataUrl(blob)
    downloadDataUrl(dataUrl, filename)
    return
  }
  downloadBlob(blob, filename)
}

// iOS Safari ignores the `download` attribute for blob: URLs and — critically —
// throws "WebKitBlobResource error 1" if the object URL is revoked before Safari
// finishes reading it (the previous code revoked synchronously right after click).
// Fix: (1) open in a new tab so the app is never navigated into a broken blob page
// and the user can save/share the PDF, and (2) defer the revoke well past the read.
// Desktop browsers still honor `download` and save the file directly.
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  a.rel = 'noopener'
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Do NOT revoke synchronously — that is what triggers WebKitBlobResource error 1
  // on iOS Safari. Defer it long enough for the download/preview to complete.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

// Print one or more composited images via a new window (reliable: prints <img>s).
export function printImageDataUrls(dataUrls: string[]): void {
  const win = window.open('', '_blank')
  if (!win) throw new Error('Could not open print window')
  const imgs = dataUrls
    .map(u => `<img src="${u}" style="width:100%;page-break-after:always" />`)
    .join('')
  win.document.write(
    `<html><head><title>Print</title><style>@page{margin:10mm}body{margin:0}img{display:block}</style></head>` +
    `<body onload="window.focus();window.print()">${imgs}</body></html>`,
  )
  win.document.close()
}

export function sanitizeFilename(name: string): string {
  return (name || 'sheet').replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '') || 'sheet'
}
